import React from 'react';
import PropTypes from 'prop-types';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

import { Spacer } from '@zillow/constellation';

import GraphConstants from 'app/shared/constants/GraphConstants';
import GraphEmptyMessage from 'app/shared/components/Graphs/GraphEmptyMessage';
import GraphTitle from 'app/shared/components/Graphs/GraphTitle';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import subMonths from 'date-fns/sub_months';
import format from 'date-fns/format';

const propTypes = {
    data: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
    showTabs: PropTypes.bool,
};

const defaultProps = {
    isLoading: false,
    showTabs: false,
};

const GRAPH = GraphConstants.GRAPHS.VIEW;
const TABS = GraphConstants.GRAPH_TABS;
const dataKey = 'Views';

const formatAverage = num => (isNaN(num) ? 0 : Math.round(num * 10) / 10);

const ViewGraph = ({ isLoading, showTabs, data }) => {
    const [activeTab, setActiveTab] = React.useState(showTabs ? TABS.AVERAGE : TABS.DEFAULT);

    React.useEffect(() => {
        if (!showTabs) {
            setActiveTab(TABS.DEFAULT);
        }
    }, [showTabs]);

    const handleTabClick = index => setActiveTab(index === 0 ? TABS.AVERAGE : TABS.TOTAL);

    const mappedData = data.map((item, i) => ({
        [dataKey]: activeTab === TABS.AVERAGE
            ? formatAverage(item.pageViews / item.activeListings) : item.pageViews,
        name: format(subMonths(new Date(), 11 - i), 'MMM YYYY'),
    }));

    const renderGraph = () => (
        <div>
            {!data.length && <GraphEmptyMessage message="No views available" />}
            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={mappedData} key={dataKey}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <Tooltip />
                    <Line
                        dataKey={dataKey}
                        stroke={GraphConstants.COLORS.zillow.secondary}
                        dot={{ r: 5, fill: GraphConstants.COLORS.zillow.secondary }}
                        activeDot={{ r: 8 }}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );

    const configs = GRAPH[activeTab];
    const total = mappedData.map(item => item[dataKey]).reduce((a, b) => a + b, 0);
    const averageDenominator = mappedData.filter(item => !!item[dataKey]).length;
    const totalCount = activeTab === TABS.AVERAGE
        ? formatAverage(total / averageDenominator) : total;
    const title = `${configs.TITLE_PREFIX}: ${totalCount || '-'}`;

    return (
        <Spacer marginY="md">
            <GraphTitle
                title={title}
                isLoading={isLoading}
                showTabs={showTabs}
                onTabClick={handleTabClick}
                activeIndex={configs.TAB_INDEX}
            />
            {isLoading ? <SpinnerContainer /> : renderGraph()}
        </Spacer>
    );
};

ViewGraph.propTypes = propTypes;
ViewGraph.defaultProps = defaultProps;

export default ViewGraph;
